import * as React from 'react';
import type { SxProps } from '@mui/material';
import { Avatar, Box, Chip, Skeleton, Stack, Typography } from '@mui/material';
import { Buildings as BuildingsIcon } from '@phosphor-icons/react/dist/ssr/Buildings';

import type { Account } from '@/types/v2/account';

interface AccountSettingsHeaderProps {
  account: Account;
  isLoading: boolean;
  error: Error | null;
  sx?: SxProps;
}

export function AccountSettingsHeader({
  account,
  isLoading,
  sx,
}: AccountSettingsHeaderProps): React.JSX.Element {
  if (isLoading) {
    return (
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center', ...sx }}>
        <Skeleton height={48} variant="rounded" width={48} />
        <Stack spacing={0.5}>
          <Skeleton height={24} variant="text" width={200} />
          <Skeleton height={18} variant="text" width={300} />
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack direction="row" spacing={2} sx={{ alignItems: 'center', ...sx }}>
      <Avatar
        variant="rounded"
        sx={{ width: 48, height: 48, bgcolor: 'primary.main', fontSize: '1rem', fontWeight: 700 }}
      >
        {account.name ? (
          account.name.split(' ').slice(0, 2).map((w) => w.charAt(0)).join('')
        ) : (
          <BuildingsIcon size={22} />
        )}
      </Avatar>
      <Box>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
            {account?.name}
          </Typography>
          <Chip label={`ID: ${account.id}`} size="small" variant="outlined" />
        </Stack>
        {account?.address && (
          <Typography variant="body2" color="text.secondary">
            {account.address} {account.city}, {account.state} {account.zip_code}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}
